import { useState } from 'react';
import api from '../../hooks/useApi';

// A draft timesheet for a period that has none, built from the contractor's
// PLOW and the time they logged in Ops (src/lib/timesheetFromPlow.js has the
// rules). It is saved as draft weeks on their portal: they check and submit,
// and the contract's approver signs off as usual. Shown under Timesheets in
// Period, and kept mounted across the refresh so the result stays on screen.

const fmtDay = (d) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const fmtDays = (n) => (Number.isInteger(n) ? `${n}d` : `${n.toFixed(1)}d`);

const SOURCE = {
  both:   { label: 'PLOW and logged time', cls: 'bg-green-50 text-green-700' },
  logged: { label: 'Logged time only',     cls: 'bg-amber-50 text-amber-700' },
  plow:   { label: 'PLOW only',            cls: 'bg-amber-50 text-amber-700' },
};

export default function TimesheetBuilder({ invoiceId, workerName, hasTimesheets, onBuilt }) {
  const [proposal, setProposal] = useState(null);
  const [state, setState] = useState(null); // 'loading' | 'saving' | null
  const [message, setMessage] = useState(null); // { tone, text }

  const open = async () => {
    setState('loading'); setMessage(null);
    try {
      setProposal(await api.get(`resource-invoices/${invoiceId}/timesheet-proposal`));
    } catch (e) {
      setMessage({ tone: 'error', text: e.message || 'The PLOW and logged time could not be read.' });
    } finally {
      setState(null);
    }
  };

  const save = async () => {
    setState('saving'); setMessage(null);
    try {
      const res = await api.post(`resource-invoices/${invoiceId}/build-timesheet`, {});
      setProposal(null);
      setMessage({ tone: 'ok', text: `Saved ${res.weeks} draft week${res.weeks === 1 ? '' : 's'} (${fmtDays(res.days)}) to ${workerName}'s portal. They count once ${workerName} submits them and the approver signs them off.` });
      onBuilt();
    } catch (e) {
      setMessage({ tone: 'error', text: e.message || 'The timesheet could not be saved.' });
    } finally {
      setState(null);
    }
  };

  const buildable = (proposal?.weeks || []).filter(w => !w.existing && w.days.some(d => !d.skipped && d.days > 0));
  const total = buildable.reduce((s, w) => s + w.days.filter(d => !d.skipped).reduce((t, d) => t + d.days, 0), 0);

  return (
    <div className="mt-2">
      {!proposal && (
        <button onClick={open} disabled={!!state}
          className={hasTimesheets
            ? 'text-[9px] text-blue-600 hover:underline disabled:opacity-40'
            : 'text-[9px] px-2.5 py-1 rounded border border-gray-300 bg-white text-gray-600 font-semibold hover:bg-gray-50 disabled:opacity-40'}>
          {state === 'loading' ? 'Reading the PLOW and logged time…'
            : hasTimesheets ? 'Build any missing weeks from the PLOW and logged time'
            : 'Build a draft timesheet from the PLOW and logged time'}
        </button>
      )}
      {message && <div className={`text-[9px] mt-1 ${message.tone === 'error' ? 'text-red-600' : 'text-green-700'}`}>{message.text}</div>}

      {proposal && (
        <div className="bg-gray-50 rounded p-3 mt-1">
          <div className="text-[8px] font-semibold text-gray-500 uppercase mb-1">Draft timesheet for {proposal.worker_name}{proposal.contract_reference ? ` on ${proposal.contract_reference}` : ''}</div>
          {proposal.blocked ? (
            <div className="text-[9px] text-amber-700">{proposal.blocked}</div>
          ) : (
            <>
              <div className="text-[8px] text-gray-500 mb-2">
                Logged time decides each day: 7 hours or more is a whole day, less is a half day. The PLOW marks evenings (a shift starting at 4pm or later).
                A day found in only one of the two is included and flagged.
              </div>
              {proposal.earlier && (
                <div className="text-[8px] text-gray-500 mb-2">
                  Days before {fmtDay(proposal.weeks[0].week_start)} are in the week of {fmtDay(proposal.earlier.week_start)}, which Timesheets in Period counts with the period before, so it is not built here.
                </div>
              )}
              {proposal.weeks.map(w => (
                <div key={w.week_start} className="mb-2">
                  <div className="text-[9px] font-semibold text-navy">Week of {fmtDay(w.week_start)}</div>
                  {w.existing ? (
                    <div className="text-[9px] text-gray-500">Already has a timesheet ({w.existing}), so it is left as it is.</div>
                  ) : !w.days.length ? (
                    <div className="text-[9px] text-gray-400">Nothing on the PLOW and no time logged.</div>
                  ) : w.days.map(d => (
                    <div key={d.date} className="flex items-start gap-2 text-[9px] py-0.5">
                      <span className="text-gray-600 whitespace-nowrap" style={{ width: 72 }}>{fmtDay(d.date)}</span>
                      <span className={`font-semibold whitespace-nowrap ${d.skipped ? 'text-gray-400 line-through' : 'text-navy'}`} style={{ width: 30 }}>{fmtDays(d.days || (d.plow?.days ?? 0))}</span>
                      <span className={`text-[7px] px-1.5 py-0.5 rounded font-semibold whitespace-nowrap ${SOURCE[d.source].cls}`}>{SOURCE[d.source].label}</span>
                      {d.evening && <span className="text-[7px] px-1.5 py-0.5 rounded font-semibold bg-amber-50 text-amber-700">Evening</span>}
                      <span className="text-gray-500 min-w-0">
                        {d.hours > 0 && `${d.hours}h logged`}
                        {d.hours > 0 && d.plow?.jobs?.length ? ' · ' : ''}
                        {(d.plow?.jobs || []).map(j => j.sow_reference ? `${j.name} (${j.sow_reference})` : j.name).join(', ')}
                        {d.flags.map((f, i) => <span key={i} className="block text-amber-700">{f}</span>)}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
          <div className="flex items-center gap-2 mt-2 text-[9px] flex-wrap">
            {!proposal.blocked && buildable.length > 0 && (
              <button onClick={save} disabled={!!state} className="px-2.5 py-1 rounded bg-navy text-white font-semibold disabled:opacity-40">
                {state === 'saving' ? 'Saving…' : `Save ${buildable.length} draft week${buildable.length === 1 ? '' : 's'} (${fmtDays(total)}) for ${proposal.worker_name} to submit`}
              </button>
            )}
            {!proposal.blocked && !buildable.length && <span className="text-gray-500">There is nothing to build for this period.</span>}
            <button onClick={() => setProposal(null)} className="text-gray-500 hover:underline">{buildable.length && !proposal.blocked ? 'Not now' : 'Close'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
